import { ReactNode, useRef, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { 
  Shield, 
  AlertTriangle, 
  Lightbulb, 
  CheckCircle2, 
  Info,
  ArrowRight,
  Flame,
  Package,
  Move,
  Monitor
} from "lucide-react";

interface ContentBlock {
  type: 'heading' | 'paragraph' | 'tip' | 'warning' | 'list' | 'callout';
  content: string;
  items?: string[];
  icon?: string;
}

interface ScrollRevealSectionProps {
  title: string;
  description?: string;
  imageUrl?: string;
  htmlContent?: string;
  sectionNumber?: number;
}

// Hook for intersection observer
function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Once visible, stop observing
          if (ref.current) observer.unobserve(ref.current);
        }
      },
      { threshold, rootMargin: "0px 0px -50px 0px" }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

// Individual reveal block component
function RevealBlock({ 
  children, 
  delay = 0,
  className 
}: { 
  children: ReactNode; 
  delay?: number;
  className?: string;
}) {
  const { ref, isInView } = useInView(0.1);

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-700 ease-out",
        isInView 
          ? "opacity-100 translate-y-0" 
          : "opacity-0 translate-y-8",
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// Parse HTML content and extract meaningful sections
function parseHtmlToBlocks(html: string): ContentBlock[] {
  if (!html) return [];
  
  const blocks: ContentBlock[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Extract text content in a structured way
  const walker = doc.createTreeWalker(
    doc.body,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    null
  );

  let currentList: string[] = [];
  let currentHeading = '';
  
  const processNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const tagName = el.tagName.toLowerCase();
      
      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        if (currentList.length > 0) {
          blocks.push({ type: 'list', content: currentHeading, items: currentList });
          currentList = [];
          currentHeading = '';
        }
        currentHeading = el.textContent?.trim() || '';
        if (currentHeading && !currentHeading.includes(':')) {
          blocks.push({ type: 'heading', content: currentHeading });
          currentHeading = '';
        }
      }
      
      if (tagName === 'li') {
        const text = el.textContent?.trim() || '';
        if (text) currentList.push(text);
      }
      
      if (tagName === 'p') {
        if (currentList.length > 0) {
          blocks.push({ type: 'list', content: currentHeading, items: currentList });
          currentList = [];
          currentHeading = '';
        }
        const text = el.textContent?.trim() || '';
        if (text && text.length > 20) {
          blocks.push({ type: 'paragraph', content: text });
        }
      }

      if (tagName === 'strong' || tagName === 'b') {
        const text = el.textContent?.trim() || '';
        if (text && text.endsWith(':')) {
          if (currentList.length > 0 && currentHeading) {
            blocks.push({ type: 'list', content: currentHeading, items: currentList });
            currentList = [];
          }
          currentHeading = text.replace(':', '');
        }
      }
    }
  };

  while (walker.nextNode()) {
    processNode(walker.currentNode);
  }

  // Add any remaining list
  if (currentList.length > 0) {
    blocks.push({ type: 'list', content: currentHeading, items: currentList });
  }

  return blocks;
}

// Get icon for list heading
function getIconForHeading(heading: string) {
  const lower = heading.toLowerCase();
  if (lower.includes('fire') || lower.includes('exit')) return Flame;
  if (lower.includes('heavy') || lower.includes('object')) return Package;
  if (lower.includes('movement') || lower.includes('lift') || lower.includes('carrying')) return Move;
  if (lower.includes('workstation') || lower.includes('setup')) return Monitor;
  if (lower.includes('warning') || lower.includes('hazard') || lower.includes('danger')) return AlertTriangle;
  if (lower.includes('tip') || lower.includes('remember')) return Lightbulb;
  if (lower.includes('safety')) return Shield;
  return CheckCircle2;
}

export function ScrollRevealSection({
  title,
  description,
  imageUrl,
  htmlContent,
  sectionNumber
}: ScrollRevealSectionProps) {
  const blocks = htmlContent ? parseHtmlToBlocks(htmlContent) : [];

  return (
    <div className="space-y-6 py-4">
      {/* Section Header */}
      <RevealBlock>
        <div className="relative">
          <div className="flex items-start gap-4">
            {sectionNumber && (
              <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 shrink-0">
                <span className="text-lg font-bold text-primary">{sectionNumber}</span>
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground leading-tight">
                {title}
              </h2>
              {description && (
                <p className="mt-2 text-muted-foreground leading-relaxed">
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>
      </RevealBlock>

      {/* Section Image */}
      {imageUrl && (
        <RevealBlock delay={100}>
          <div className="overflow-hidden rounded-xl border shadow-sm">
            <img 
              src={imageUrl}
              alt={title}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        </RevealBlock>
      )}

      {/* Content Blocks */}
      <div className="space-y-5">
        {blocks.map((block, index) => {
          const delay = 150 + (index * 100);

          if (block.type === 'heading') {
            return (
              <RevealBlock key={index} delay={delay}>
                <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
                  <div className="w-1 h-5 bg-primary rounded-full" />
                  {block.content}
                </h3>
              </RevealBlock>
            );
          }

          if (block.type === 'paragraph') {
            return (
              <RevealBlock key={index} delay={delay}>
                <p className="text-muted-foreground leading-relaxed pl-0 sm:pl-3">
                  {block.content}
                </p>
              </RevealBlock>
            );
          }

          if (block.type === 'list' && block.items) {
            const Icon = getIconForHeading(block.content);
            return (
              <RevealBlock key={index} delay={delay}>
                <div className="rounded-xl bg-gradient-to-br from-muted/50 to-muted/20 border border-border/50 p-4 sm:p-5 space-y-4">
                  {block.content && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <h4 className="font-medium text-foreground">{block.content}</h4>
                    </div>
                  )}
                  <ul className="space-y-3">
                    {block.items.map((item, itemIndex) => (
                      <li 
                        key={itemIndex}
                        className="flex items-start gap-3 text-muted-foreground"
                      >
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </RevealBlock>
            );
          }

          return null;
        })}

        {/* Fallback: if no blocks parsed, show raw content nicely */}
        {blocks.length === 0 && htmlContent && (
          <RevealBlock delay={150}>
            <div 
              className="prose prose-sm max-w-none dark:prose-invert leading-relaxed"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </RevealBlock>
        )}
      </div>

      {/* Progress indicator line */}
      <RevealBlock delay={blocks.length * 100 + 200}>
        <div className="flex items-center gap-3 pt-4">
          <div className="flex-1 h-px bg-gradient-to-r from-border via-primary/30 to-border" />
          <CheckCircle2 className="h-4 w-4 text-primary/50" />
          <div className="flex-1 h-px bg-gradient-to-r from-border via-primary/30 to-border" />
        </div>
      </RevealBlock>
    </div>
  );
}

export { RevealBlock, useInView };
